<script lang="ts" setup>
import { ref, reactive, computed, defineEmits, onMounted, watch } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { EConsoleType, useConsoleMessage } from '@/composables/useConsoleMessage'
import {
    OrganizationMs,
    useFetchApiRequestAsync,
    useOrganizations,
    useProfileUpdate
} from 'auth-package'
import i18n from '@/i18n'

interface Props {
    organizationUUId?: string
    isEdit?: boolean
}

const props = defineProps<Props>()
const organizations = useOrganizations()

const { t } = i18n.global

interface RuleForm {
    name: string
    description: string
    icon: string
}

const organizationFormRef = ref<FormInstance>()
const organizationForm = reactive<RuleForm>({
    name: '',
    description: '',
    icon: ''
})

const rules = reactive<FormRules<RuleForm>>({
    name: [
        { required: true, message: t('settings.requiredField'), trigger: 'blur' },
        {
            min: 1,
            max: 255,
            message: t('settings.minMaxCharacters', { text: t('settings.organizationCharacters') }),
            trigger: 'change'
        }
    ],
    description: [
        {
            min: 1,
            max: 255,
            message: t('settings.minMaxCharacters', { text: t('settings.organizationCharacters') }),
            trigger: 'change'
        }
    ]
})

function initForm() {
    if (props.isEdit && props.organizationUUId) {
        const organization = organizations.value[props.organizationUUId]

        organizationForm.name = organization.name || ''
        organizationForm.description = organization.description || ''
        organizationForm.icon = organization.icon || ''
    }
}

onMounted(() => {
    initForm()
})

watch(
    () => props.organizationUUId,
    () => {
        initForm()
    }
)

/**
 * Validates the form and shows an error message if the form is invalid.
 * If the form is valid, then create a new campaign.
 * @param formEl - The form element.
 */
async function onSubmit() {
    if (!organizationFormRef.value) {
        return
    }

    let response

    await organizationFormRef.value.validate(async (valid, fields) => {
        if (valid) {
            if (props.isEdit) {
                await updateOrganization()
            } else {
                response = await createOrganization()
            }
        } else {
            console.log('Error: ', fields)
        }
    })

    return response
}

/**
 * Creates a new organization with the given name and description.
 * After the organization is created, it closes the dialog and shows a success message.
 * If an error occurs, it shows an error message.
 */
async function createOrganization() {
    try {
        const createOrganization = OrganizationMs.CreateOrganization().setData(organizationForm)
        const response = await useFetchApiRequestAsync(createOrganization)

        if (response) {
            setTimeout(async () => {
                await useProfileUpdate()
            }, 1500)
            resetForm()

            useConsoleMessage(
                EConsoleType.SUCCESS,
                'createOrganization',
                'Successfully created the organization!'
            )

            return response
        }
    } catch (error) {
        useConsoleMessage(EConsoleType.ERROR, 'createOrganization', error)
    }
}

async function updateOrganization() {
    if (props.organizationUUId == null) {
        return
    }

    try {
        const updateOrganization = OrganizationMs.UpdateOrganization()
            .setParameters({ organizationId: props.organizationUUId })
            .setData(organizationForm)
        const response = await useFetchApiRequestAsync(updateOrganization)

        if (response) {
            setTimeout(async () => {
                await useProfileUpdate()
            }, 1500)
            
            useConsoleMessage(
                EConsoleType.SUCCESS,
                'updateOrganization',
                'Successfully updated the organization!'
            )
        }
    } catch (error) {
        useConsoleMessage(EConsoleType.ERROR, 'updateOrganization', error)
    }
}

/**
 * Resets the form to initial state.
 * @param formEl - The form element.
 */
function resetForm() {
    if (!organizationFormRef.value) {
        return
    }

    organizationFormRef.value.resetFields()
}

defineExpose({
    onSubmit
})
</script>

<template>
    <el-form
        ref="organizationFormRef"
        label-position="top"
        :model="organizationForm"
        :rules="rules"
        hide-required-asterisk
    >
        <el-form-item prop="icon">
            <prizific-image-picker
                v-model="organizationForm.icon"
                :title="$t('settings.uploadIcon')"
                :UUId="organizationUUId"
                type="organization"
                hidePublicSwitch
            />
        </el-form-item>
        <el-form-item prop="name" :label="$t('settings.name')">
            <el-input
                v-model="organizationForm.name"
                :placeholder="$t('settings.name')"
                maxlength="255"
                show-word-limit
            />
        </el-form-item>
        <!-- <el-form-item prop="description" :label="$t('settings.description')">
            <el-mention
                v-model="organizationForm.description"
                :placeholder="$t('settings.description')"
                maxlength="255"
                type="textarea"
                show-word-limit
            />
        </el-form-item> -->
    </el-form>
</template>

<style lang="scss" scoped></style>
